import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { v4 as randomId } from 'uuid';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { CustomAlert, CustomButton, Picker, Text } from '../../../../../shared/uiComponents';
import { dateNormalizer, errorNormalizer } from '../../../../../shared/Helpers/functions';
import AuthorizationDetail from './authorizationDetail';
import AddAuthorizationContent from './addAuthorizationContent';

import './index.scss';

import { DispatchProperties } from '../../../../../redux/store';
import {
	getInsuranceAuthorizationSessionTypes,
	getInsuranceAuthorizations,
	updateAuthorization,
} from '../../../../../redux/State/clientSlice/insuranceAuthorizationSlice';
import {
	ActiveAuthorizationProperties,
	AddAuthorizationDetailsProperties,
	UpdateInsuranceAuthorization,
} from '../../../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';

export interface EditAuthorizationProperties extends AddAuthorizationDetailsProperties {
	id: string;
}

const EditAuthorizationContent = ({
	authorizationData,
	onClose,
}: {
	authorizationData: ActiveAuthorizationProperties;
	onClose: () => void;
}) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();
	const [params] = useSearchParams();
	const { startDate, endDate, id } = authorizationData;

	const [openAdd, setOpenAdd] = useState<boolean>(false);
	const [loading, setLoading] = useState<boolean>(false);
	const [startDateValue, setStartDateValue] = useState<Dayjs>(dayjs(startDate));
	const [endDateValue, setEndDateValue] = useState<Dayjs>(dayjs(endDate));
	const [details, setDetails] = useState<EditAuthorizationProperties[]>(
		authorizationData.authorizationDetails.map((x) => ({
			id: randomId(),
			authorizationSessionTypeId: x.sessionType.id,
			cptCode: x.cptCode,
			frequency: x.frequency.id,
			totalHours: x.totalHours,
			hoursPerWeek: x.frequency.id === 1 ? x.hoursPerWeek : null,
			sessionTypeId: x.sessionTypeId,
		})),
	);

	const onChangeTreatmentStartDate = (value: Dayjs | null) => {
		if (!value) return;
		setStartDateValue(value);
	};

	const onChangeTreatmentEndDate = (value: Dayjs | null) => {
		if (!value) return;
		setEndDateValue(value);
	};

	const updateInsuranceAuthorization = () => {
		if (!clientId) return;
		const startDateUpdateValue = dateNormalizer(startDateValue);
		const endDateUpdateValue = dateNormalizer(endDateValue);

		const data: UpdateInsuranceAuthorization = {
			clientId,
			authorizationId: id,
			authorizationDetails: details,
			endDate: endDateUpdateValue,
			startDate: startDateUpdateValue,
			funderId: authorizationData.funderId ?? null,
		};
		setLoading(true);
		dispatch(updateAuthorization(data))
			.then(unwrapResult)
			.then(() => {
				toast('Success');
				onClose();
				const page = params.get('page') || '1';
				const pageSize = params.get('pageSize') || '8';
				dispatch(getInsuranceAuthorizations({ clientId, page, pageSize }));
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	const updateAuthorizationDetailsHandler = useCallback(
		(item: EditAuthorizationProperties) => {
			const updatedData = details.map((detail) => {
				if (detail.id !== item.id) {
					return detail;
				}

				return item;
			});
			setDetails(updatedData);
		},
		[details],
	);

	const addAuthorizationDetailsHandler = useCallback(
		(item: EditAuthorizationProperties) => {
			setDetails([...details, item]);
			setOpenAdd(false);
		},
		[details],
	);

	const removeAuthorizationDetailsHandler = useCallback(
		(item: EditAuthorizationProperties) => {
			const updatedData = details.filter((x) => x.id !== item.id);
			setDetails(updatedData);
		},
		[details],
	);

	useEffect(() => {
		dispatch(getInsuranceAuthorizationSessionTypes(`${authorizationData.type.id}`));
	}, [dispatch, authorizationData]);

	return (
		<>
			<Text
				title={`Funding Source: ${authorizationData.funderName ?? 'Insurance'}`}
				type="psm"
				fontWeight="semiBold"
				className="marginBottom16"
			/>
			<div className="dateRow marginBottom16">
				<Picker.CustomDate label="Start Date:" value={startDateValue} onChange={onChangeTreatmentStartDate} />
				<Picker.CustomDate label="End Date:" value={endDateValue} onChange={onChangeTreatmentEndDate} />
			</div>
			<div className="actionsRow marginBottom16">
				<CustomButton title="Save" onClick={updateInsuranceAuthorization} loading={loading} />
				{authorizationData.type.id === 2 && <CustomButton title="Add New" onClick={() => setOpenAdd(true)} />}
			</div>
			{details.map((x) => (
				<AuthorizationDetail
					key={x.id}
					item={x}
					startDateValue={startDateValue}
					endDateValue={endDateValue}
					authorizationTypeId={authorizationData.type.id}
					funderId={authorizationData.funderId}
					update={updateAuthorizationDetailsHandler}
					remove={removeAuthorizationDetailsHandler}
				/>
			))}
			<CustomAlert
				title="Add"
				open={openAdd}
				onClose={() => setOpenAdd(false)}
				Content={() => (
					<AddAuthorizationContent
						endDateValue={endDateValue}
						startDateValue={startDateValue}
						update={addAuthorizationDetailsHandler}
						funderId={authorizationData.funderId}
					/>
				)}
			/>
		</>
	);
};

export default EditAuthorizationContent;
